import { PropsWithChildren, useEffect, useState } from 'react';
import type { StyleProp, TextProps, TextStyle } from 'react-native';
import { AppState, PixelRatio, Platform, StyleSheet, Text } from 'react-native';
import { cn as dsCn } from '@vidahealth/vida-ds';

import { useAuthLayout } from '../contexts/AuthLayoutContext';
import { useDsEnabled } from '../contexts/DesignSystem';
import { cn } from '../utils/cn';

export type TextColor = 'default' | 'dark' | 'subtle' | 'link' | 'white' | 'error' | 'success' | 'warning';

export const TEXT_TYPES = [
  'titleHero',
  'titleDefault',
  'subtitleDefault',
  'subtitleSmall',
  'bodyRegular',
  'bodyBold',
  'bodySmall',
  'bodySmallBold',
  'buttonDefault',
  'buttonLight',
  'buttonSmall',
  'footnote',
  'overline',
] as const;

export type TextType = (typeof TEXT_TYPES)[number];

export type ThemedTextProps = TextProps & {
  color?: TextColor;
  type?: TextType;
};

const typographyClasses: Record<TextType, string> = {
  titleHero: 'text-login-hero font-semibold leading-login-hero tracking-login-hero font-login-body',
  titleDefault: 'text-login-title font-semibold leading-login-title font-login-body',
  subtitleDefault: 'text-lg font-bold leading-login-subtitle font-login-body',
  subtitleSmall: 'text-base font-bold font-login-body',
  bodyRegular: 'text-base font-normal leading-login-body font-login-body',
  bodyBold: 'text-base font-semibold leading-login-body font-login-body',
  bodySmall: 'text-sm font-normal leading-login-body-sm font-login-body',
  bodySmallBold: 'text-sm font-semibold leading-login-body-sm font-login-body',
  buttonDefault: 'text-base font-semibold leading-login-button tracking-login-button font-login-body',
  buttonLight: 'text-base font-normal tracking-login-button font-login-body',
  buttonSmall: 'text-sm font-semibold tracking-login-button-sm font-login-body',
  footnote: 'text-login-footnote font-medium leading-login-footnote tracking-login-footnote font-login-body',
  overline: 'text-login-footnote font-bold tracking-login-overline uppercase font-login-body',
};

const defaultColorClasses: Partial<Record<TextType, string>> = {
  overline: 'text-login-gray-70',
  footnote: 'text-login-gray-70',
  buttonSmall: 'text-login-purple-50',
  titleHero: 'text-login-purple-90',
  titleDefault: 'text-login-purple-90',
};

const colorOverrideClasses: Record<TextColor, string | undefined> = {
  default: undefined,
  dark: 'text-login-gray-90',
  subtle: 'text-login-gray-50',
  link: 'text-login-purple-50',
  white: 'text-login-surface-pure',
  error: 'text-login-red-50',
  success: 'text-login-green-50',
  warning: 'text-login-gold-50',
};

const dsTypographyClasses: Record<TextType, string> = {
  titleHero: 'type-h3',
  titleDefault: 'type-h5',
  subtitleDefault: 'type-h6',
  subtitleSmall: 'type-h6',
  bodyRegular: 'type-b2',
  bodyBold: 'type-b2-strong',
  bodySmall: 'type-b3',
  bodySmallBold: 'type-b3-strong',
  buttonDefault: 'type-button-1',
  buttonLight: 'type-button-1',
  buttonSmall: 'type-button-2',
  footnote: 'type-label-1',
  overline: 'type-label-2 uppercase',
};

const dsDefaultColorClasses: Partial<Record<TextType, string>> = {
  overline: 'text-secondary',
  footnote: 'text-secondary',
};

const dsColorOverrideClasses: Record<TextColor, string | undefined> = {
  default: undefined,
  dark: 'text-primary',
  subtle: 'text-secondary',
  link: 'text-link',
  white: 'text-inverse',
  error: 'text-status-error',
  success: 'text-status-success',
  warning: 'text-status-warning',
};

// Screens tune legacy type with these utilities and inline font metrics; vida-ds type tokens own both.
const LEGACY_TYPE_CLASS = /^(font-|leading-|tracking-|text-login-|text-(xs|sm|base|lg|\d?xl)$)/;

const stripLegacyType = (className: string | undefined) =>
  className?.split(/\s+/).filter((c) => !LEGACY_TYPE_CLASS.test(c)).join(' ');

const stripFontMetrics = (style: StyleProp<TextStyle>): TextStyle => {
  const flat = { ...StyleSheet.flatten(style) };
  delete flat.fontSize;
  delete flat.lineHeight;
  delete flat.fontWeight;
  delete flat.letterSpacing;
  delete flat.fontFamily;
  return flat;
};

export default function ThemedText(props: PropsWithChildren<ThemedTextProps>) {
  const dsEnabled = useDsEnabled();
  const { inSheet } = useAuthLayout();
  // NativeWind's CSS runtime caches styles and won't re-render when the system
  // font scale changes. Keying on fontScale forces a remount when it changes.
  const [fontScale, setFontScale] = useState(PixelRatio.getFontScale());

  useEffect(() => {
    const sub = AppState.addEventListener('change', (state) => {
      if (state === 'active') {
        setFontScale(PixelRatio.getFontScale());
      }
    });
    return () => sub.remove();
  }, []);

  const {
    color = 'default',
    type = 'bodyRegular',
    numberOfLines,
    children,
    style,
    className,
    ...rest
  } = props;

  // On native, flexShrink defaults to 0 so text never collapses below its
  // content height. On web, CSS defaults flexShrink to 1, which can shrink
  // the element and then overflow:hidden (applied by numberOfLines) clips it.
  const needsShrinkGuard = Platform.OS === 'web' && numberOfLines != null;

  if (dsEnabled) {
    const dsColorClass = dsColorOverrideClasses[color] ?? dsDefaultColorClasses[type] ?? 'text-primary';
    // The mobile sheet sets its heading a step down, as in the Figma sheet frames.
    const dsTypeClass = inSheet && type === 'titleHero' ? 'type-h4' : dsTypographyClasses[type];
    return (
      <Text
        key={fontScale}
        numberOfLines={numberOfLines}
        className={dsCn(
          dsTypeClass,
          dsColorClass,
          needsShrinkGuard && 'shrink-0',
          stripLegacyType(className)
        )}
        style={stripFontMetrics(style)}
        {...rest}
      >
        {children}
      </Text>
    );
  }

  const colorClass = colorOverrideClasses[color] ?? defaultColorClasses[type] ?? 'text-login-gray-90';

  return (
    <Text
      key={fontScale}
      numberOfLines={numberOfLines}
      className={cn(typographyClasses[type], colorClass, needsShrinkGuard && 'shrink-0', className)}
      style={style}
      {...rest}
    >
      {children}
    </Text>
  );
}
